import classNames from 'classnames';
import { useState, useRef } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Picture, StatusMessage, useViewport } from '@farmersdog/corgi-x';
import { useInterval } from 'src/hooks/useInterval/useInterval';
import { useTrackedSectionRef } from '../../../../hooks/useTrackedSections';
import { HomepageRedesignSections } from '../CtaTrackerV2';
import { useShowHomepageRedesign } from 'src/hooks/useHomepageRedesign';

import { CHANGE_ITEM_INTERVAL_SECONDS } from './config';
import { pressBarItems } from './pressBarItems';
import { PressBarItem } from './PressBarItem';
import { ChevronButton } from './ChevronButton';

import styles from './PressBarSection.module.css';

const ariaQuoteElementId = 'quote-element-id';

// Ensures that the modulo operation always returns a positive number we can use as array indexes
const safeModulo = (n: number, m: number) => ((n % m) + m) % m;

export const PressBarSection = () => {
  const { isVetsNegativeTreatment, isVetsPositiveTreatment } =
    useShowHomepageRedesign();
  const viewport = useViewport();
  const isMobile = !viewport.md;

  const [isTriggered, setIsTriggered] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const itemCount = pressBarItems.length;

  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // For tracking section visibility
  const sectionRef = useTrackedSectionRef(HomepageRedesignSections.PressBar);

  // Advance to the next item automatically
  useInterval({
    durationSeconds: CHANGE_ITEM_INTERVAL_SECONDS,
    onTimeLapsed: () => {
      setActiveIndex(prev => prev + 1);
    },
    enabled: isTriggered && !isPaused,
  });

  const activeItem = pressBarItems[safeModulo(activeIndex, itemCount)];

  return (
    <section
      className={classNames(styles.section, {
        [styles.sectionVetsNegative]: isVetsNegativeTreatment,
        [styles.sectionVetsPositive]: isVetsPositiveTreatment,
      })}
      ref={sectionRef}
    >
      <div
        className={classNames(styles.container, {
          [styles.containerVetsNegative]: isVetsNegativeTreatment,
          [styles.containerVetsPositive]: isVetsPositiveTreatment,
        })}
        ref={containerRef}
      >
        <div className={styles.prevButtonContainer}>
          <ChevronButton
            orientation="left"
            onClick={() => setActiveIndex(prev => prev - 1)}
            ariaLabel="Previous quote"
          />
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeItem.alt}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: 'easeInOut' }}
            className={styles.slide}
            onViewportEnter={() => {
              setIsTriggered(true);
            }}
            onTouchStart={() => setIsPaused(true)}
            onTouchEnd={() => setIsPaused(false)}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocus={() => setIsPaused(true)}
            onBlur={() => setIsPaused(false)}
          >
            <div
              className={classNames(styles.slideImageContainer, {
                [styles.slideImageContainerVetsNegative]:
                  isVetsNegativeTreatment,
                [styles.slideImageContainerVetsPositive]:
                  isVetsPositiveTreatment,
              })}
            >
              <Picture
                sources={activeItem.sources}
                alt={activeItem.alt}
                style={{
                  height: isMobile
                    ? activeItem.height.mobile
                    : activeItem.height.desktop,
                }}
              />
            </div>
            <div
              className={classNames(styles.footnoteContainer, {
                [styles.footnoteContainerVetsNegative]: isVetsNegativeTreatment,
                [styles.footnoteContainerVetsPositive]: isVetsPositiveTreatment,
              })}
              onClick={() => setIsPaused(false)}
              tabIndex={0}
            >
              <StatusMessage
                key={activeIndex}
                id={ariaQuoteElementId}
                politeness="polite"
                aria-label={activeItem.ariaLabel}
              >
                <div aria-hidden className={styles.footnote}>
                  <PressBarItem quote={activeItem.quote} />
                </div>
              </StatusMessage>
            </div>
          </motion.div>
        </AnimatePresence>
        <div className={styles.nextButtonContainer}>
          <ChevronButton
            orientation="right"
            onClick={() => setActiveIndex(prev => prev + 1)}
            ariaLabel="Next quote"
          />
        </div>
      </div>
    </section>
  );
};
